import {
  isAgentSignedOutMessage,
  isPreconditionReason,
  type PreconditionReason,
} from '@crowdex/agent-contracts';
import { errorMessage } from '@/lib/error-message';

/** A failure as the conversation shows it: its message, and what is missing when that is known. */
export type ConversationFailure = { message: string; reason: PreconditionReason | null };

/**
 * What is missing for a failure to go away: the `reason` the server answered with
 * (`ApiClientError`, `PreconditionError`), else an agent's own "not signed in" words, which reach
 * the page as plain stream text.
 */
export function failureReason(error: unknown): PreconditionReason | null {
  const reason = (error as { reason?: unknown } | null)?.reason;
  if (isPreconditionReason(reason)) return reason;
  return isAgentSignedOutMessage(errorMessage(error)) ? 'agent_signed_out' : null;
}

export function conversationFailure(error: unknown): ConversationFailure {
  return { message: errorMessage(error), reason: failureReason(error) };
}

/** The headline of a failure the user can fix; the message says the rest. */
export function preconditionTitle(reason: PreconditionReason, agentName: string | null): string {
  switch (reason) {
    case 'no_agent':
      return 'No agent can answer here.';
    case 'agent_signed_out':
      return `${agentName ?? 'The agent'} is not signed in.`;
    case 'no_model_provider':
      return 'No model makes this.';
    case 'model_provider_missing':
      return 'The chosen model provider is gone.';
    case 'folder_missing':
      return 'This folder is not on this device.';
    case 'not_a_repository':
      return 'This folder is not a Git repository.';
    case 'cloud_unlinked':
      return 'Crowdex Cloud is not reachable from here.';
    case 'cloud_project_missing':
      return 'Crowdex Cloud has no copy of this project.';
  }
}
